/** @jsxImportSource @emotion/react */
import {useDispatch, useSelector} from 'react-redux';
import {IAppState} from '../../core/redux/IAppState';
import {toUserAr} from './module';
import {getError, getLoading} from '../../core/redux/api/module';
import {ACTION_TYPE} from '../../core/redux/actionType';
import {UserAr} from './userAr';
import {useEffect, useMemo, useState} from 'react';
import {getUsersAction} from './getUsersAction';
import {ApiError} from '../../core/redux/api/apiError';
import {clearApiErrorAction} from '../../core/redux/api/clearApiErrorAction';
import {showSnackbarAction} from '../../snackbar/snackbarAction';
import {addUserAction} from './addUserAction';
import {updateUserAction} from './updateUserAction';
import {deleteUserAction} from './deleteUserAction';
import {PropValidator} from '../../forms/validation/propValidation';
import {validateNewUser} from './validation';
import {mergeDeepRight} from 'ramda';
import ConfigRedirect from '../../auth/ConfigRedirect';
import {CONFIG_KEY} from '../../auth/config/configKey';
import {ROOT_PATH} from '../../core/routes';
import {Column} from '../../layout/flexbox';
import {PAGE_COLUMN, PAGE_CONTENT, PAGE_TITLE, XXXLargeText} from '../../styles/styles';
import {UserManagementTable} from './UserManagementTable';
import {css} from '@emotion/react';
import {Fade} from '../../styles/animations';
import BlockingLoader from '../../forms/elements/loader/BlockingLoader';
import {isNilOrEmpty, sortByEmailProp, sortByProp} from '../../language/module';
import {StyledBoxedMessage} from '../../forms/elements/boxedMessage/StyledBoxedMessage';
import ConfirmationModal from '../../forms/elements/ConfirmationModal';
import * as React from 'react';
import {LIGHT_GREY, YELLOW} from '../../styles/colors';
import {updatePasswordAction} from './updatePasswordAction';
import {getUserAction} from '../../auth/getUserInfoAction';
import {blockUserAction} from './blockUserAction';
import {unblockUserAction} from './unblockUserAction';
import TablePagination from '@mui/material/TablePagination';
import { UserSearchBox } from './UserSearchBox';
import { TooltipFC } from '../../layout/TooltipFC';
import infoImg from '../../assets/icons/info.svg';
import { getResource } from '../../language/resources/getResource';
import { shallowEqual } from 'react-redux';

const ACTIONS = [
  ACTION_TYPE.ADD_USER_REQUEST,
  ACTION_TYPE.UPDATE_USER_REQUEST,
  ACTION_TYPE.REMOVE_USER_REQUEST,
  ACTION_TYPE.BLOCK_USER_REQUEST,
  ACTION_TYPE.UNBLOCK_USER_REQUEST,
  ACTION_TYPE.UPDATE_PASSWORD_REQUEST,
] as string[];

const noUsers = css`
  text-align: center;
  font-style: italic;
  ${XXXLargeText};
  width: 100%;
  display: block;
  padding: 20px 0;
  background-color: ${LIGHT_GREY};
`;

const overridemuifont = css `
  .MuiTablePagination-selectLabel {
    font-size: 1rem;
  }
  .MuiTablePagination-root {
    font-size: 1rem;
  }
  .MuiTablePagination-displayedRows {
    font-size: 1rem;
  }
  .MuiTablePagination-actions {
    font-size: 1rem;
  }
`;

const infoStyle = css`
  width: 20px;
  height: 20px;
  margin-bottom: -3px; /* align image with text */
  margin-left: 5px;
  pointer-events: initial;
  font-size: 16px;
`;

const userSearchAlignment = css`
  display: inline-flex; 
  align-items: end; 
  justify-content: space-between;
`;

const userSearchBoxAlignment = css`
  align-items:center; 
  justify-content: flex-start; 
  margin-left: 50px; 
  margin-bottom: 10px;
`;

const userAccountDisabled = css`
  display: flex; 
  align-items: 
  center; gap: 5px
`;

const userAccountDisabledColorIndicator = css`
  height: 12px; 
  width: 12px; 
  background-color: ${YELLOW}
`;

const fetchUserFailedMessageBox = css`
  margin-top: 25px; 
  margin-left: auto; 
  margin-right: auto;
`;

const tablePagination = css`
  align-items: center; 
  justify-content: flex-end; 
  margin-right: 50px; 
  ${overridemuifont}
`;


export const UserManagementPage = () => {

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [usersPerPage, setUsersPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [filteredUsers, setFilteredUsers] = useState([].map(toUserAr));
  const [filterError, setFilterError] = useState('');
  const [pagedUsers, setPagedUsers] =  useState([].map(toUserAr));
  const [upForDeletion, setUpForDeletion] = useState<UserAr | undefined>(undefined);
  const [upForAddition, setUpForAddition] = useState<string>('');
  const dispatch = useDispatch();
  const roles = useSelector((state: IAppState) => state.resortToolsConfig.userRoles.slice(state.resortToolsConfig.userRoles.indexOf(state.userState.role!)));
  const rawUsers = useSelector((state: IAppState) => 
    (state.users || []).filter(user => roles.includes(user.role)), shallowEqual
  );
  const users = useMemo(() => rawUsers.map(toUserAr), [rawUsers]);  
  const usernames = useSelector((state: IAppState) => (state.users || []).map(user => user.userName), shallowEqual);
  const emails = useSelector((state: IAppState) => (state.users || []).map(user => user.email), shallowEqual);
  const isLoadingUsers = useSelector((state: IAppState) => !!getLoading(state, ACTION_TYPE.GET_USERS_REQUEST, ACTION_TYPE.GET_USERS_REQUEST));
  const isAddingUser = useSelector((state: IAppState) => !!getLoading(state, upForAddition, ACTION_TYPE.ADD_USER_REQUEST));
  const errorUsers = useSelector((state: IAppState) => getError(state, ACTION_TYPE.GET_USERS_REQUEST, ACTION_TYPE.GET_USERS_REQUEST));
  const crudLoading = useSelector((state: IAppState) => !state.loading.filter((key, action) => ACTIONS.includes(action.actionType)).isEmpty());
  const crudErrors = useSelector((state: IAppState) => state.error.filter((key, action) => ACTIONS.includes(action.actionType)).getValues());
  const selectedResort = useSelector((state: IAppState) => state.selectedResort);
  const userResorts = useSelector((state: IAppState) => state.resortDetails?.allResorts.getValues().filter(res => state.userState.resortLocations?.includes(res.resortId)));
  const allResorts = useSelector((state: IAppState) => state.resortDetails?.allResorts.getValues());
  const loggedInUser = useSelector((state: IAppState) => state.userState);
  const resources = useSelector((state: IAppState) => state.locale);

  const onTooltipClick = (event) => event.preventDefault();

  useEffect(() => {
    setCurrentPage(0);
    dispatch(getUsersAction(selectedResort.resortId));
  },[selectedResort, dispatch])

  useEffect(() => {
    const showError = (errorMsg: string) => dispatch(showSnackbarAction(errorMsg, true));
    const clearError = (error: ApiError) => dispatch(clearApiErrorAction(error.requestId, error.actionType));
    if (crudErrors.length > 0 ) {
      crudErrors.map(clearError);

      const failedUsernames = crudErrors.reduce((running, next) => running + next.requestId + ', ', '').slice(0, -2);

      showError(getResource(resources, 'USER_MANAGEMENT_FAILED_USERNAMES_ERROR', failedUsernames));
    }
  }, [crudErrors, resources, dispatch]);

  useEffect(() => { // if the logged-in users role changes their view of the app permissions need to reload
    const usersUser = users.find(user => user.userId === loggedInUser.userId);
    if ( usersUser && usersUser.role !== loggedInUser.role) {
      dispatch(getUserAction(loggedInUser.userId))
    }
  }, [users, dispatch, loggedInUser])

  useEffect(() => {
    if (!isLoadingUsers && !crudLoading) {
      setFilteredUsers(users);
    }
  }, [isLoadingUsers, crudLoading, users])

  useEffect(() => { 
    if (!isLoadingUsers) {
      setPagedUsers(filteredUsers.slice(currentPage * usersPerPage, (currentPage+1) * usersPerPage));
    }
  }, [currentPage, usersPerPage, filteredUsers, isLoadingUsers])

  useEffect(() => {
    const showSnackbar = (message: string) => dispatch(showSnackbarAction(message));
    if (!isAddingUser && crudErrors.filter(error => error.actionType === ACTION_TYPE.ADD_USER_REQUEST).length === 0 && upForAddition !== '') {
      showSnackbar(getResource(resources, 'USER_MANAGEMENT_CREATE_USER_SUCCESS', upForAddition));
      setUpForAddition('');
    }
  }, [isAddingUser, crudErrors, resources, upForAddition, dispatch]);
    
  const handleUserPageChange  = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number,  ) => {
    setCurrentPage(newPage);
  };

  const handleUsersPerPageSelection = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, ) => {
    setUsersPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const sortUsers = (property: keyof UserAr, asc: boolean) => {
    const sortedUsers = property === 'email' ?
       sortByEmailProp(true, asc, filteredUsers) :
       sortByProp(property, true, asc, filteredUsers);

    return sortedUsers.slice(currentPage * usersPerPage, (currentPage + 1) * usersPerPage);
  }

  const onCreateUser = (user: UserAr) => {
    dispatch(addUserAction(user));
    setUpForAddition(user.userName);
  };

  const onUpdateUser = (user: UserAr, isUpdatingPassword: boolean) => {
    if (isUpdatingPassword) {
      dispatch(updatePasswordAction(user.userId, user.password))
    }
    dispatch(updateUserAction(user));
  }

  const deleteUser = (user: UserAr) => {
    setShowConfirmationModal(true);
    setUpForDeletion(user);
  }

  const blockUser = (userId: string) => {
    dispatch(blockUserAction(userId));
  }

  const unblockUser = (userId: string) => {
    dispatch(unblockUserAction(userId));
  }

  const onUserSearch = (query: string) => { 
    setFilterError('');
    const filtered = users.filter(user => new RegExp(query, "i").test(user.userName) || new RegExp(query, "i").test(user.firstName) 
      || new RegExp(query, "i").test(user.lastName) || (new RegExp(query, "i").test(user.email) && user.hasEmail));
    setCurrentPage(0);
    setFilteredUsers(filtered);

    if (filtered.length === 0) {
      setFilterError(getResource(resources, 'USER_MANAGEMENT_FILTER_ERROR', query));
    }
    else {
      setFilterError('');
    }
  }

  const onClearUserSearch = () => {
    setFilterError('');
    setCurrentPage(0);
    setFilteredUsers(users);
  }


  const dismissModal = () => setShowConfirmationModal(false)
  const onConfirmDeleteUser = () => {
    if (!upForDeletion) return;
    dispatch(deleteUserAction(upForDeletion.userId));
    dismissModal()
  }

  const validateUser: PropValidator<UserAr> = (
    user: UserAr,
    extras: any,
  ) => validateNewUser(user, mergeDeepRight(extras, {usernames: usernames, roles: roles, emails: emails}));

  return (
    <ConfigRedirect configPath={CONFIG_KEY.MANAGE_USERS} redirectRoute={ROOT_PATH}>
      <Column rootCss={css`${PAGE_COLUMN} width: fit-content; min-width: 100%;`}>
        <span css={PAGE_TITLE}>{getResource(resources, 'USER_MANAGEMENT_PAGE_TITLE')} 
          <TooltipFC
              content={<span>{getResource(resources, 'USER_MANAGEMENT_PAGE_INFO')}</span>}
              children={<img alt='ⓘ' src={infoImg} css={infoStyle} onClick={onTooltipClick} />}/>
        </span>
          
          <div css={userSearchAlignment}>
            <div css={userSearchBoxAlignment}>
              <UserSearchBox onUserSearch={onUserSearch} errorMessage={filterError} onClearUserSearch={onClearUserSearch}/>
            </div>
            <div css={tablePagination}>         
              <TablePagination
                component="div"
                count={filteredUsers!.length | 0}
                page={currentPage}
                onPageChange={handleUserPageChange}
                rowsPerPage={usersPerPage}
                onRowsPerPageChange={handleUsersPerPageSelection}
                labelRowsPerPage={getResource(resources, 'USER_MANAGEMENT_USERS_PER_PAGE')}
                rowsPerPageOptions={[10,25,50]}
                showFirstButton
                showLastButton
              />
            </div>
          </div>
        <div css={PAGE_CONTENT}>       
          <UserManagementTable roles={roles!}
                               users={pagedUsers!}
                               onUpdateUser={onUpdateUser}
                               onCreateUser={onCreateUser}
                               onDeleteUser={deleteUser!}
                               onBlockUser={blockUser}
                               onUnblockUser={unblockUser}
                               onSortUsers={sortUsers}
                               isLoadingUsers={isLoadingUsers}
                               validateUser={validateUser}
                               rootCss={css`margin: 20px;`}
                               userResorts={userResorts || []}
                               allResorts={allResorts || []}
          />
          <div css={userAccountDisabled}>
            <span>{getResource(resources, 'USER_MANAGEMENT_ACCOUNT_DISABLED')}</span>
            <div css={userAccountDisabledColorIndicator}></div>
          </div>
          {/* Loading CRUD operation */}
          <Fade in={crudLoading}><BlockingLoader key='blockingLoader'/></Fade>
          { // No Users
            isNilOrEmpty(users)
            && !isLoadingUsers
            && <div>
              <span css={noUsers}>{getResource(resources, 'USER_MANAGEMENT_NO_USERS')}</span>
            </div>
          }
          { // Load Users Error
            !isLoadingUsers
            && errorUsers
            && <StyledBoxedMessage extraCss={fetchUserFailedMessageBox}
                                   message={getResource(resources, 'USER_MANAGEMENT_FETCH_USERS_FAILED')}/>
          }
        </div>
        {
          showConfirmationModal
          && !!upForDeletion
          && <ConfirmationModal
            resources={resources}
            title={getResource(resources, 'USER_MANAGEMENT_REMOVE_USER_TITLE')}
            message={getResource(resources, 'USER_MANAGEMENT_REMOVE_USER_MSG', upForDeletion.firstName)}
            positiveButtonText={getResource(resources, 'USER_MANAGEMENT_REMOVE_BUTTON')}
            negativeButtonText={getResource(resources, 'USER_MANAGEMENT_CANCEL_REMOVE_BUTTON')}
            onPositiveButtonClick={onConfirmDeleteUser}
            onNegativeButtonClick={dismissModal}
            onDismiss={dismissModal}/>
        }
      </Column>
    </ConfigRedirect>
  );
}